import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { heroConfig as defaultHeroConfig } from '@/config';
import type { HeroConfig } from '@/config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useConfig } from '@/lib/siteData';
import { trackEvent } from '@/lib/plausible';
import { imageVariants, withWebp } from '@/lib/imageSrc';

// Hero v4 — dual-mode (lens | scrim) with admin-tunable bg positioning,
// per-theme overlay + text colors, and a fully-tunable lens window.
//
// The hunt animation visits three corners of the section, then settles on
// the configured lens position. All lens math is relative to the SECTION
// dimensions; --lens-width / --lens-height use vw so width and height scale
// together (and can be set independently for a rectangular lens).
const HUNT_INITIAL_DELAY_MS = 220;
const HUNT_STEP_MS = 360;

function buildHuntPath(lensXPct: number, lensYPct: number): Array<{ x: string; y: string }> {
  return [
    // [0] top-left corner of the section
    { x: '0%', y: '0%' },
    // [1] top-right corner
    { x: 'calc(100% - var(--lens-width))', y: '0%' },
    // [2] bottom-right corner
    { x: 'calc(100% - var(--lens-width))', y: 'calc(100% - var(--lens-height))' },
    // [3] configured lens anchor — center on (lensXPct, lensYPct).
    {
      x: `calc(${lensXPct}% - var(--lens-width) / 2)`,
      y: `calc(${lensYPct}% - var(--lens-height) / 2)`,
    },
  ];
}

// ─── HeroView — pure prop-driven render ────────────────────────
// Receives a fully-resolved HeroConfig as a prop, does no data fetching.
// Used directly by the admin live-preview pane and indirectly by `Hero`
// (which wraps it with useConfig + field-level fallbacks).
export function HeroView({ config }: { config: HeroConfig }) {
  const prefersReducedMotion = useReducedMotion();

  const name = config.name || '';
  const roleLine = config.role?.trim() || '';
  const headline = config.headline?.trim() || '';
  const description = config.description?.trim() || '';
  const primaryCtaLabel = config.primaryCtaLabel?.trim() || '';
  const primaryCtaHref = config.primaryCtaHref?.trim() || '/contact';
  const secondaryCtaLabel = config.secondaryCtaLabel?.trim() || '';
  const secondaryCtaHref = config.secondaryCtaHref?.trim() || '/services';
  const positioningLine = config.positioningLine?.trim() ?? '';

  const headlineMaxWidth = config.headlineMaxWidth;
  // Background image transform: bgImageOffsetX/Y are the new admin-tunable
  // numeric fields; fall back to the legacy backgroundPositionX +
  // portraitObjectPositionY when a row hasn't been migrated yet.
  const bgX =
    typeof config.bgImageOffsetX === 'number'
      ? config.bgImageOffsetX
      : config.backgroundPositionX;
  const bgY =
    typeof config.bgImageOffsetY === 'number'
      ? `${config.bgImageOffsetY}%`
      : config.portraitObjectPositionY || '30%';
  const bgScale = typeof config.bgImageScale === 'number' ? config.bgImageScale : 1;
  const faceY = bgY;
  const heroMode = config.heroMode;
  const bgBlurPx = config.backgroundBlurPx;
  const overlayEnabled = config.overlayEnabled;
  const overlayDirection = config.overlayDirection;
  const lensXPct = config.lensPositionXPercent;
  const lensYPct = config.lensPositionYPercent;
  const lensW = config.lensWidthPercent;
  const lensH = config.lensHeightPercent;

  const huntPath = buildHuntPath(lensXPct, lensYPct);

  const [isLoaded, setIsLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [huntIndex, setHuntIndex] = useState(() =>
    prefersReducedMotion ? huntPath.length - 1 : 0,
  );
  const sectionRef = useRef<HTMLElement>(null);

  // Fade-in once mounted.
  useEffect(() => {
    const t = setTimeout(() => setIsLoaded(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Hunt animation — only meaningful in lens mode.
  useEffect(() => {
    if (heroMode !== 'lens') return;
    if (prefersReducedMotion) return;
    if (!isLoaded) return;
    let stepTimer: number | null = null;

    const initial = window.setTimeout(() => {
      setHuntIndex(1);
      stepTimer = window.setInterval(() => {
        setHuntIndex((i) => {
          const next = i + 1;
          if (next >= huntPath.length - 1 && stepTimer !== null) {
            window.clearInterval(stepTimer);
            stepTimer = null;
          }
          return Math.min(next, huntPath.length - 1);
        });
      }, HUNT_STEP_MS);
    }, HUNT_INITIAL_DELAY_MS);

    return () => {
      window.clearTimeout(initial);
      if (stepTimer !== null) window.clearInterval(stepTimer);
    };
  }, [heroMode, isLoaded, prefersReducedMotion, huntPath.length]);

  if (config.visible === false) return null;
  if (!name && !headline) return null;

  const currentAnchor = huntPath[huntIndex] ?? huntPath[huntPath.length - 1];

  // Section-root CSS variables. `--hero-text-color` and `--hero-overlay-*`
  // are picked between their `-light` / `-dark` per-theme variants in
  // `index.css` via the `[data-theme]` selector — admins don't need JS to
  // resolve which one is active.
  const sectionStyle: React.CSSProperties = {
    '--hero-bg-pos-x': `${bgX}%`,
    '--hero-bg-pos-y': faceY,
    '--hero-bg-blur': `${bgBlurPx}px`,
    '--hero-overlay-color-light': config.overlayColorLight,
    '--hero-overlay-color-dark': config.overlayColorDark,
    '--hero-overlay-opacity-light': String(config.overlayOpacityLight / 100),
    '--hero-overlay-opacity-dark': String(config.overlayOpacityDark / 100),
    '--hero-text-color-light': config.textColorLight,
    '--hero-text-color-dark': config.textColorDark,
  } as React.CSSProperties;

  const backdropStyle = {
    '--lens-width': `${lensW}vw`,
    '--lens-height': `${lensH}vw`,
    '--lens-x': currentAnchor.x,
    '--lens-y': currentAnchor.y,
  } as React.CSSProperties;

  // Sharp clip-path uses --lens-x / --lens-y as offsets from the section's
  // top-left corner, and --lens-width / --lens-height for the window size.
  const lensClipPath =
    'inset(' +
    'var(--lens-y) ' +
    'calc(100% - var(--lens-x) - var(--lens-width)) ' +
    'calc(100% - var(--lens-y) - var(--lens-height)) ' +
    'var(--lens-x))';

  // Position the lens frame via top/left so CSS `%` resolves against the
  // backdrop wrapper (not the lens box itself, as it would with translate3d).
  const lensFrameTransition = prefersReducedMotion
    ? 'opacity 1000ms ease'
    : 'top 480ms cubic-bezier(0.45, 0, 0.18, 1), left 480ms cubic-bezier(0.45, 0, 0.18, 1), opacity 1000ms ease';

  const sharpImgTransition = prefersReducedMotion
    ? 'opacity 1000ms ease'
    : 'clip-path 480ms cubic-bezier(0.45, 0, 0.18, 1), -webkit-clip-path 480ms cubic-bezier(0.45, 0, 0.18, 1), opacity 1000ms ease';

  const objectPosition = `${bgX}% ${faceY}`;
  // Applied to both <img> layers so the lens stays aligned with the backdrop.
  // overflow: hidden on the wrapper section + backdrop keeps zoom > 1 cropped.
  const bgTransform = `scale(${bgScale})`;
  // webp variants for the backdrop, when the configured path is one the build
  // generated variants for. Null (admin-uploaded /uploads/*, external URL, an
  // /images/* file with no variants) renders the plain <img> below unchanged —
  // <picture> has no runtime fallback, so we must not guess a .webp path.
  // Both layers are full-bleed, hence 100vw.
  const bgVariants = imageVariants(config.backgroundImage, '100vw');
  const headlineMaxWidthStyle = { maxWidth: `${headlineMaxWidth}px` };
  const textColorStyle = { color: 'var(--hero-text-color)' } as React.CSSProperties;

  // Scrim gradient — built from overlayDirection + the per-theme color/opacity
  // CSS variables. `color-mix` is supported in evergreen browsers; project
  // already uses comparable modern CSS.
  const scrimGradient = (() => {
    const stop = 'color-mix(in srgb, var(--hero-overlay-color) calc(var(--hero-overlay-opacity) * 100%), transparent)';
    switch (overlayDirection) {
      case 'right-left':
        return `linear-gradient(270deg, ${stop} 0%, transparent 65%)`;
      case 'top-bottom':
        return `linear-gradient(180deg, ${stop} 0%, transparent 70%)`;
      case 'radial':
        return `radial-gradient(ellipse at center, ${stop} 0%, transparent 70%)`;
      case 'left-right':
      default:
        return `linear-gradient(90deg, ${stop} 0%, ${stop.replace('* 100%', '* 65%')} 35%, transparent 65%)`;
    }
  })();

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-reduced-motion={prefersReducedMotion ? 'true' : undefined}
      data-hero-mode={heroMode}
      className="relative w-full overflow-hidden bg-hero-bg pt-16 lg:pt-24 pb-16 lg:pb-24"
      style={sectionStyle}
    >
      {/* Full-bleed backdrop. In lens mode this hosts the blurred image PLUS
          a sharp clip-pathed copy PLUS the viewfinder. In scrim mode it just
          hosts the blurred image and the scrim overlay. */}
      <div
        data-testid="hero-backdrop"
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={backdropStyle}
      >
        {/* Layer 1 — blurred bg image. Both modes use it.
            fetchPriority='high' on the image itself replaces the global
            <link rel='preload'> that index.html used to carry: that preload
            fired on every SPA route's first paint and produced a console
            warning everywhere except '/'. The browser's preload scheduler
            picks this up early enough that LCP doesn't regress. */}
        {withWebp(
          bgVariants,
          <img
            src={config.backgroundImage}
            alt=""
            aria-hidden="true"
            width={1344}
            height={768}
            fetchPriority="high"
            onLoad={() => setImageLoaded(true)}
            className={cn(
              'absolute inset-0 w-full h-full object-cover transition-opacity duration-1000',
              isLoaded && imageLoaded ? 'opacity-100' : 'opacity-0',
            )}
            style={{
              objectPosition,
              transform: bgTransform,
              transformOrigin: 'center center',
              filter: `blur(${bgBlurPx}px)`,
            }}
          />,
        )}

        {heroMode === 'lens' && (
          <>
            {/* Layer 2 — sharp bg image clip-path'd to the lens window. */}
            {withWebp(
              bgVariants,
              <img
                src={config.backgroundImage}
                alt={name ? `${name} portrait` : 'Portrait'}
                width={1344}
                height={768}
                className={cn(
                  'absolute inset-0 w-full h-full object-cover transition-opacity duration-1000',
                  isLoaded && imageLoaded ? 'opacity-100' : 'opacity-0',
                )}
                style={{
                  objectPosition,
                  transform: bgTransform,
                  transformOrigin: 'center center',
                  clipPath: lensClipPath,
                  WebkitClipPath: lensClipPath,
                  transition: sharpImgTransition,
                  willChange: 'clip-path',
                }}
              />,
            )}

            {/* Layer 3 — viewfinder frame (border + crosshair + corner ticks). */}
            <div
              data-testid="hero-lens-frame"
              className={cn(
                'absolute',
                'w-[var(--lens-width)] h-[var(--lens-height)]',
                'border border-hero-lens-frame',
                isLoaded && imageLoaded ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                top: 'var(--lens-y)',
                left: 'var(--lens-x)',
                transition: lensFrameTransition,
                willChange: 'top, left',
                boxShadow:
                  'inset 0 0 0 1px var(--hero-lens-glow), 0 24px 60px var(--hero-lens-glow)',
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3 h-px bg-hero-lens-cross" />
                <div className="absolute w-px h-3 bg-hero-lens-cross" />
              </div>
              <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-hero-lens-tick" />
              <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-hero-lens-tick" />
              <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-hero-lens-tick" />
              <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-hero-lens-tick" />
            </div>
          </>
        )}

        {heroMode === 'scrim' && overlayEnabled && (
          <div
            data-testid="hero-scrim"
            data-direction={overlayDirection}
            className="absolute inset-0"
            style={{ background: scrimGradient }}
          />
        )}
      </div>

      {/* Content grid — sits above the backdrop via z-index. */}
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-12">
        <div className="grid gap-10 lg:gap-12 lg:grid-cols-12 lg:items-center">
          <div
            data-testid="hero-content"
            className="lg:col-span-7 flex flex-col"
          >
            <div
              className={cn(
                'transition-all duration-1000 ease-out',
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
              )}
              style={{ transitionDelay: '500ms' }}
            >
              <p
                className="font-geist-mono text-xs sm:text-sm uppercase tracking-[0.25em]"
                style={textColorStyle}
                aria-label={name}
              >
                {name}
              </p>
              {roleLine && (
                <p className="mt-2 font-body text-sm" style={textColorStyle}>{roleLine}</p>
              )}
            </div>

            <h1
              className={cn(
                'mt-6 font-heading font-semibold tracking-tight leading-[1.05]',
                'text-[clamp(2rem,4.6vw,3.75rem)]',
                'transition-all duration-1000 ease-out',
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
              )}
              style={{ ...headlineMaxWidthStyle, ...textColorStyle, transitionDelay: '650ms' }}
            >
              {headline}
            </h1>

            {description && (
              <p
                className={cn(
                  'mt-5 lg:mt-6 font-body text-base lg:text-lg leading-relaxed',
                  'transition-all duration-1000 ease-out',
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
                )}
                style={{ ...headlineMaxWidthStyle, ...textColorStyle, transitionDelay: '850ms' }}
              >
                {description}
              </p>
            )}

            <div
              className={cn(
                'mt-8 flex flex-wrap items-center gap-3 transition-all duration-1000 ease-out',
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
              )}
              style={{ transitionDelay: '1050ms' }}
            >
              <Link
                to={primaryCtaHref}
                onClick={() => trackEvent('Hero CTA Click', { label: primaryCtaLabel, cta: 'primary' })}
                className={cn(
                  'inline-flex items-center justify-center',
                  'px-6 py-3 rounded-[10px]',
                  'bg-hero-cta-primary-bg text-hero-cta-primary-fg',
                  'text-sm font-medium',
                  'transition-colors duration-150',
                  'hover:bg-hero-cta-primary-hover',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-hero-bg focus-visible:ring-hero-cta-primary-bg',
                )}
              >
                {primaryCtaLabel}
              </Link>

              {/* Rendered only when a label is set — unlike the primary, which
                  always renders. There are no hero-cta-secondary-* tokens, so
                  this uses the hero text colour with a hairline border to stay
                  legible over both the lens and scrim backgrounds. */}
              {secondaryCtaLabel && (
                <Link
                  to={secondaryCtaHref}
                  onClick={() => trackEvent('Hero CTA Click', { label: secondaryCtaLabel, cta: 'secondary' })}
                  className={cn(
                    'inline-flex items-center justify-center',
                    'px-6 py-3 rounded-[10px]',
                    'border text-sm font-medium',
                    'transition-colors duration-150',
                    // Was `hover:bg-hero-cta-primary-bg/10`, which emitted nothing:
                    // hero tokens are hand-tuned rgba composites, not alpha-composable
                    // channel triplets. --hero-cta-outline-bg-hover is the wash that
                    // already exists for exactly this, per theme.
                    'hover:bg-[color:var(--hero-cta-outline-bg-hover)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-hero-bg focus-visible:ring-hero-cta-primary-bg',
                  )}
                  style={{ ...textColorStyle, borderColor: 'var(--hero-hairline)' }}
                >
                  {secondaryCtaLabel}
                </Link>
              )}
            </div>

            {positioningLine && (
              <div
                className={cn(
                  'mt-10 lg:mt-12 transition-all duration-1000 ease-out',
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
                )}
                style={{ ...headlineMaxWidthStyle, transitionDelay: '1200ms' }}
              >
                <div
                  aria-hidden="true"
                  className="h-px w-full"
                  style={{ backgroundColor: 'var(--hero-hairline)' }}
                />
                <p
                  className="mt-4 font-geist-mono text-[13px]"
                  style={{ ...textColorStyle, opacity: 0.85 }}
                >
                  {positioningLine}
                </p>
              </div>
            )}
          </div>

          {/* Right column — kept on lg+ to reserve space for the lens / face;
              hidden on mobile so the text doesn't push down a full empty row. */}
          <div
            data-testid="hero-portrait"
            aria-hidden="true"
            className="hidden lg:block lg:col-span-5"
          />
        </div>
      </div>
    </section>
  );
}

// ─── Hero — public wrapper used by routes ─────────────────────
// Fetches via useConfig, applies per-field fallbacks (so a blank admin
// value doesn't blank the live site), then renders HeroView.
export function Hero() {
  const heroConfig = useConfig('hero_config', defaultHeroConfig);

  // Per-field fallbacks for fields the admin might intentionally clear.
  // Strict schema fields (numbers, booleans, enums) are already required so
  // they always have a value; only string-bearing content fields need this.
  const resolved: HeroConfig = {
    ...defaultHeroConfig,
    ...heroConfig,
    headline: heroConfig.headline?.trim() || defaultHeroConfig.headline,
    description: heroConfig.description?.trim() || defaultHeroConfig.description,
    primaryCtaLabel: heroConfig.primaryCtaLabel?.trim() || defaultHeroConfig.primaryCtaLabel,
    primaryCtaHref: heroConfig.primaryCtaHref?.trim() || defaultHeroConfig.primaryCtaHref,
    // No `||` fallback on the secondary label: an admin clearing it must hide
    // the button, not silently restore the default.
    secondaryCtaLabel: heroConfig.secondaryCtaLabel ?? defaultHeroConfig.secondaryCtaLabel,
    secondaryCtaHref: heroConfig.secondaryCtaHref?.trim() || defaultHeroConfig.secondaryCtaHref,
    portraitObjectPositionY:
      heroConfig.portraitObjectPositionY?.trim() || defaultHeroConfig.portraitObjectPositionY,
  };

  return <HeroView config={resolved} />;
}
